import PageMeta from "../components/PageMeta";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LuPlus } from "react-icons/lu";
import PropertyRequestCard from "../components/propertyRequest/PropertyRequestCard";
import { fieldClass } from "../components/propertyRequest/PropertyRequestFields";
import { categoryLabel, cityLabel } from "../components/propertyRequest/labels";
import { TRANSACTIONS, categoriesFor, cities } from "../utils/propertyRequestForm";
import { propertyRequestApi } from "../Services/propertyRequestService";

const PAGE_SIZE = 20;

// Public list of active requests (read only; proposing a property comes in a later phase).
export default function PropertyRequests() {
  const { t } = useTranslation();
  const [phoneFilters, setPhoneFilters] = useState(false);
  const [params, setParams] = useSearchParams();
  const transaction = params.get("transaction") || "";
  // "Vacances" only offers the 3 vacation types; a type of another transaction is not kept in the filter.
  const categoryOptions = categoriesFor(transaction);
  const category = categoryOptions.includes(params.get("category")) ? params.get("category") : "";
  const filters = { transaction, category, city: params.get("city") || "" };
  const page = Math.max(0, Number.parseInt(params.get("page") || "0", 10) || 0);
  const [state, setState] = useState({ loading: true, error: "", items: [], total: 0 });
  const key = params.toString();

  useEffect(() => {
    let current = true;
    setState((s) => ({ ...s, loading: true, error: "" }));
    propertyRequestApi.list(filters, page, PAGE_SIZE)
      .then((r) => { if (current) setState({ loading: false, error: "", items: r.items, total: r.total }); })
      .catch((e) => { if (current) setState({ loading: false, error: e.message, items: [], total: 0 }); });
    return () => { current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const setFilter = (name, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(name, value); else next.delete(name);
    if (name === "transaction" && !categoriesFor(value).includes(next.get("category"))) next.delete("category");
    next.delete("page");
    setParams(next);
  };
  const toPage = (value) => {
    const next = new URLSearchParams(params);
    if (value > 0) next.set("page", String(value)); else next.delete("page");
    setParams(next);
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  };
  const cityOptions = useMemo(() => cities().map((c) => ({ value: c, label: cityLabel(t, c) })).sort((a, b) => a.label.localeCompare(b.label)), [t]);
  const pages = Math.max(1, Math.ceil(state.total / PAGE_SIZE));

  return (
    <main className="lina-property-request mx-auto w-full max-w-5xl px-4 py-6 sm:px-6 sm:py-10">
      <PageMeta titleKey="seo.requests" path="/property-requests" />
      <h1 className="phone-results-title text-3xl font-black text-slate-900 sm:text-4xl">{t("propertyRequest.list.title")}</h1>
      <p className="mt-2 text-base text-slate-600">{t("propertyRequest.list.subtitle")}</p>
      <Link to="/property-request/new" className="lina-action-primary mt-4 h-12 w-full text-base sm:w-auto"><LuPlus aria-hidden="true" />{t("propertyRequest.list.start")}</Link>

      <div data-phone-filters={phoneFilters || undefined} className="phone-request-filters mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <button type="button" className="phone-only phone-request-toggle" aria-expanded={phoneFilters} onClick={() => setPhoneFilters(v => !v)}>{t("mobile.filters")}</button>
        <select aria-label={t("propertyRequest.criteria.transaction")} className={fieldClass} value={filters.transaction} onChange={(e) => setFilter("transaction", e.target.value)}>
          <option value="">{t("propertyRequest.list.allTransactions")}</option>
          {TRANSACTIONS.map((tx) => <option key={tx} value={tx}>{t(`propertyRequest.transactionShort.${tx}`)}</option>)}
        </select>
        <select aria-label={t("propertyRequest.criteria.categories")} className={fieldClass} value={filters.category} onChange={(e) => setFilter("category", e.target.value)}>
          <option value="">{t("propertyRequest.list.allTypes")}</option>
          {categoryOptions.map((c) => <option key={c} value={c}>{categoryLabel(t, c, filters.transaction)}</option>)}
        </select>
        <select aria-label={t("propertyRequest.criteria.city")} className={fieldClass} value={filters.city} onChange={(e) => setFilter("city", e.target.value)}>
          <option value="">{t("propertyRequest.list.allCities")}</option>
          {cityOptions.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        {state.error && <p role="alert" className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800 lg:col-span-2">{state.error}</p>}
        {!state.loading && !state.error && state.items.length === 0 && <p className="text-slate-600 lg:col-span-2">{t("propertyRequest.list.empty")}</p>}
        {state.items.map((r) => (
          <PropertyRequestCard key={r.id} request={r}>
            <div className="border-t border-slate-100 px-4 py-3 sm:px-5">
              <Link to={`/property-request/${r.id}`} className="font-semibold text-green-800 underline">{t("propertyRequest.list.view")}</Link>
            </div>
          </PropertyRequestCard>
        ))}
        {state.loading && <p role="status" className="text-slate-600">{t("propertyRequest.mine.loading")}</p>}
      </div>

      {state.total > PAGE_SIZE && (
        <nav className="mt-6 flex items-center justify-between gap-3" aria-label={t("propertyRequest.list.title")}>
          <button type="button" className="lina-action h-11" disabled={page === 0} onClick={() => toPage(page - 1)}>{t("propertyRequest.list.previous")}</button>
          <span className="text-sm text-slate-600">{t("propertyRequest.list.page", { page: page + 1, pages })}</span>
          <button type="button" className="lina-action h-11" disabled={page + 1 >= pages} onClick={() => toPage(page + 1)}>{t("propertyRequest.list.next")}</button>
        </nav>
      )}
    </main>
  );
}
